import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stAvatarDefaults,
  stBanUser,
  stCreateOfficialAccount,
  stIdentityRoles,
  stIdentityUsers,
  stSetIdentityUserRole,
  stUpdateIdentityRolePermissions,
  stVerifyUserInfoExists,
  StIdentityRole,
  StIdentityUser,
} from '../../../services/socialTradingService';
import { SocialTradingAvatarField } from '../SocialTradingAvatarField';
import {
  StIdCell,
  StActionButton,
  StActionRow,
  StBooleanChip,
  StScrollTable,
  stFormatDateTime,
  stText,
  useStConfirmAction,
} from '../StListUi';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PAGE_SIZE = 20;

const ROLE_CAPS: Array<{ key: keyof StIdentityRole; label: string }> = [
  { key: 'canBrowsePublicRoom', label: 'browsePublicRoom' },
  { key: 'canJoinRoom', label: 'joinRoom' },
  { key: 'canSendMessage', label: 'sendMessage' },
  { key: 'canCreateRoom', label: 'createRoom' },
  { key: 'canEditRoom', label: 'editRoom' },
  { key: 'canConfigPool', label: 'configPool' },
  { key: 'canSendRedPacket', label: 'sendRedPacket' },
  { key: 'canClaimRedPacket', label: 'claimRedPacket' },
  { key: 'canPlaceOrder', label: 'placeOrder' },
  { key: 'canShowOfficialBadge', label: 'showOfficialBadge' },
];

export default function SocialTradingAccountsList() {
  const t = useTranslate();
  const notify = useNotify();
  const { askConfirm, confirmDialog } = useStConfirmAction();
  const [roles, setRoles] = useState<StIdentityRole[]>([]);
  const [officials, setOfficials] = useState<StIdentityUser[]>([]);
  const [officialsTotal, setOfficialsTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [permDialog, setPermDialog] = useState<StIdentityRole | null>(null);
  const [permDraft, setPermDraft] = useState<StIdentityRole | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createUserId, setCreateUserId] = useState('');
  const [createNickname, setCreateNickname] = useState('');
  const [createAvatar, setCreateAvatar] = useState('');
  const [userIdCheck, setUserIdCheck] = useState<'idle' | 'checking' | 'ok' | 'fail'>('idle');
  const [userDefaultAvatars, setUserDefaultAvatars] = useState<string[]>([]);
  const [roleDialog, setRoleDialog] = useState<StIdentityUser | null>(null);
  const [nextRole, setNextRole] = useState('OFFICIAL');

  const loadRoles = useCallback(async () => {
    try {
      setRoles((await stIdentityRoles()) || []);
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  const loadOfficials = useCallback(async () => {
    try {
      const rows = await stIdentityUsers({
        role: 'OFFICIAL',
        keyword: keyword || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setOfficials(rows?.items || []);
      setOfficialsTotal(rows?.total || 0);
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [keyword, page, notify]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    stAvatarDefaults()
      .then((d) => setUserDefaultAvatars((d?.userAvatars || []).filter(Boolean)))
      .catch(() => setUserDefaultAvatars([]));
  }, []);

  useEffect(() => {
    loadOfficials();
  }, [loadOfficials]);

  const savePerm = async () => {
    if (!permDraft) return;
    try {
      await stUpdateIdentityRolePermissions({
        code: permDraft.code,
        name: permDraft.name,
        description: permDraft.description,
        canBrowsePublicRoom: permDraft.canBrowsePublicRoom,
        canJoinRoom: permDraft.canJoinRoom,
        canSendMessage: permDraft.canSendMessage,
        canCreateRoom: permDraft.canCreateRoom,
        canEditRoom: permDraft.canEditRoom,
        canConfigPool: permDraft.canConfigPool,
        canSendRedPacket: permDraft.canSendRedPacket,
        canClaimRedPacket: permDraft.canClaimRedPacket,
        canPlaceOrder: permDraft.canPlaceOrder,
        canShowOfficialBadge: permDraft.canShowOfficialBadge,
      });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setPermDialog(null);
      loadRoles();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const verifyCreateUserId = async (): Promise<boolean> => {
    const userId = createUserId.trim();
    if (!userId) {
      notify(t('module.socialTrading.officialNeedUserId'), { type: 'warning' });
      setUserIdCheck('fail');
      return false;
    }
    if (!UUID_RE.test(userId)) {
      notify(t('module.socialTrading.officialInvalidUserId'), { type: 'warning' });
      setUserIdCheck('fail');
      return false;
    }
    setUserIdCheck('checking');
    const ok = await stVerifyUserInfoExists(userId);
    setUserIdCheck(ok ? 'ok' : 'fail');
    if (!ok) {
      notify(t('module.socialTrading.officialUserIdNotInUserInfo'), { type: 'warning' });
    }
    return ok;
  };

  const createOfficial = async () => {
    const userId = createUserId.trim();
    if (!userId) {
      notify(t('module.socialTrading.officialNeedUserId'), { type: 'warning' });
      return;
    }
    if (!UUID_RE.test(userId)) {
      notify(t('module.socialTrading.officialInvalidUserId'), { type: 'warning' });
      return;
    }
    if (!createNickname.trim()) {
      notify(t('module.socialTrading.officialNeedNickname'), { type: 'warning' });
      return;
    }
    const nickname = createNickname.trim();
    if (nickname.length < 2 || nickname.length > 16) {
      notify(t('module.socialTrading.officialNicknameLength'), { type: 'warning' });
      return;
    }
    if (userIdCheck !== 'ok') {
      const ok = await verifyCreateUserId();
      if (!ok) return;
    }
    try {
      await stCreateOfficialAccount({
        userId,
        nickname,
        avatar: createAvatar.trim() || undefined,
      });
      setCreateOpen(false);
      setCreateUserId('');
      setCreateNickname('');
      setCreateAvatar('');
      setUserIdCheck('idle');
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      loadOfficials();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const saveRole = async () => {
    if (!roleDialog) return;
    try {
      await stSetIdentityUserRole({ userId: roleDialog.userId, role: nextRole });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setRoleDialog(null);
      loadOfficials();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const toggleBan = async (u: StIdentityUser) => {
    try {
      await stBanUser({ userId: u.userId, banned: !u.banned });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      loadOfficials();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  return (
    <PermissionGuard resource="st-accounts" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.accounts')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.accounts') },
          ]}
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                loadRoles();
                loadOfficials();
              }}
            >
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('module.socialTrading.roles')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('module.socialTrading.rolesHint')}
              </Typography>
              <StScrollTable>
                <TableHead>
                  <TableRow>
                    <TableCell>code</TableCell>
                    <TableCell>name</TableCell>
                    <TableCell>createRoom</TableCell>
                    <TableCell>configPool</TableCell>
                    <TableCell>sendRedPacket</TableCell>
                    <TableCell align="right">{t('module.socialTrading.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((r) => (
                    <TableRow key={r.code}>
                      <TableCell>{stText(r.code)}</TableCell>
                      <TableCell>{stText(r.name)}</TableCell>
                      <TableCell>
                        <StBooleanChip value={r.canCreateRoom} />
                      </TableCell>
                      <TableCell>
                        <StBooleanChip value={r.canConfigPool} />
                      </TableCell>
                      <TableCell>
                        <StBooleanChip value={r.canSendRedPacket} />
                      </TableCell>
                      <TableCell align="right">
                        <StActionRow>
                          {/* POST /social-trading/identity/roles/permissions requires st-accounts:update */}
                          <PermissionButton resource="st-accounts" action="update">
                            <StActionButton
                              onClick={() => {
                                setPermDialog(r);
                                setPermDraft({ ...r });
                              }}
                            >
                              {t('module.socialTrading.editPermissions')}
                            </StActionButton>
                          </PermissionButton>
                        </StActionRow>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </StScrollTable>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography variant="h6" sx={{ flex: 1 }}>
                  {t('module.socialTrading.officialAccounts')}
                </Typography>
                <TextField
                  size="small"
                  label={t('module.socialTrading.filterKeyword')}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const next = keywordInput.trim();
                      setKeyword(next);
                      if (page !== 0) setPage(0);
                      else if (next === keyword) loadOfficials();
                    }
                  }}
                  sx={{ minWidth: 220 }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    const next = keywordInput.trim();
                    setKeyword(next);
                    if (page !== 0) setPage(0);
                    else if (next === keyword) loadOfficials();
                  }}
                >
                  {t('module.socialTrading.search')}
                </Button>
                {/* POST /social-trading/identity/users/official requires st-users:create */}
                <PermissionButton resource="st-users" action="create">
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setCreateUserId('');
                      setCreateNickname('');
                      setCreateAvatar('');
                      setUserIdCheck('idle');
                      setCreateOpen(true);
                    }}
                  >
                    {t('module.socialTrading.createOfficial')}
                  </Button>
                </PermissionButton>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('module.socialTrading.officialAccountsHint')}
              </Typography>
              <StScrollTable>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('module.socialTrading.nickname')}</TableCell>
                    <TableCell>{t('module.socialTrading.userId')}</TableCell>
                    <TableCell>{t('module.socialTrading.colRole')}</TableCell>
                    <TableCell>{t('module.socialTrading.colAccess')}</TableCell>
                    <TableCell>{t('module.socialTrading.colUpdated')}</TableCell>
                    <TableCell align="right">{t('module.socialTrading.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {officials.map((u) => (
                    <TableRow key={u.userId}>
                      <TableCell>{stText(u.nickname)}</TableCell>
                      <TableCell><StIdCell value={u.userId} /></TableCell>
                      <TableCell>{stText(u.role)}</TableCell>
                      <TableCell>
                        <StBooleanChip
                          value={!u.banned}
                          trueLabel={t('module.socialTrading.accessEnabled')}
                          falseLabel={t('module.socialTrading.accessDisabled')}
                        />
                      </TableCell>
                      <TableCell>{stText(stFormatDateTime(u.updatedAt))}</TableCell>
                      <TableCell align="right">
                        <StActionRow>
                          {/* POST /social-trading/identity/users/role requires st-accounts:update */}
                          <PermissionButton resource="st-accounts" action="update">
                            <StActionButton
                              onClick={() => {
                                setNextRole(u.role);
                                setRoleDialog(u);
                              }}
                            >
                              {t('module.socialTrading.setRole')}
                            </StActionButton>
                          </PermissionButton>
                          {/* POST /social-trading/identity/users/ban requires st-users:update */}
                          <PermissionButton resource="st-users" action="update">
                            <StActionButton
                              color={u.banned ? 'success' : 'warning'}
                              onClick={() =>
                                askConfirm({
                                  title: t('module.socialTrading.confirmActionTitle'),
                                  content: t('module.socialTrading.confirmActionBody', {
                                    action: u.banned
                                      ? t('module.socialTrading.unban')
                                      : t('module.socialTrading.ban'),
                                  }),
                                  confirmLabel: t('module.socialTrading.confirmOk'),
                                  confirmColor: u.banned ? 'success' : 'warning',
                                  onConfirm: () => toggleBan(u),
                                })
                              }
                            >
                              {u.banned ? t('module.socialTrading.unban') : t('module.socialTrading.ban')}
                            </StActionButton>
                          </PermissionButton>
                        </StActionRow>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!officials.length && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t('module.socialTrading.emptyOfficials')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </StScrollTable>

              <TablePagination
                component="div"
                count={officialsTotal}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
            </CardContent>
          </Card>
        </Stack>

        <Dialog open={!!permDialog} onClose={() => setPermDialog(null)} fullWidth maxWidth="sm">
          <DialogTitle>
            {t('module.socialTrading.editPermissions')} · {permDraft?.code}
          </DialogTitle>
          <DialogContent>
            {permDraft && (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <TextField
                  size="small"
                  label="name"
                  value={permDraft.name}
                  onChange={(e) => setPermDraft({ ...permDraft, name: e.target.value })}
                />
                <TextField
                  size="small"
                  label="description"
                  value={permDraft.description || ''}
                  onChange={(e) => setPermDraft({ ...permDraft, description: e.target.value })}
                />
                {ROLE_CAPS.map((cap) => (
                  <FormControlLabel
                    key={cap.key}
                    control={
                      <Checkbox
                        checked={Boolean(permDraft[cap.key])}
                        onChange={(e) => setPermDraft({ ...permDraft, [cap.key]: e.target.checked })}
                      />
                    }
                    label={cap.label}
                  />
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPermDialog(null)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/identity/roles/permissions requires st-accounts:update */}
            <PermissionButton resource="st-accounts" action="update">
              <Button variant="contained" onClick={savePerm}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t('module.socialTrading.createOfficial')}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  size="small"
                  required
                  fullWidth
                  label={t('module.socialTrading.userId')}
                  value={createUserId}
                  onChange={(e) => {
                    setCreateUserId(e.target.value);
                    setUserIdCheck('idle');
                  }}
                  helperText={
                    userIdCheck === 'ok'
                      ? t('module.socialTrading.officialUserIdCheckOk')
                      : userIdCheck === 'fail'
                        ? t('module.socialTrading.officialUserIdNotInUserInfo')
                        : t('module.socialTrading.officialUserIdHint')
                  }
                  error={userIdCheck === 'fail'}
                />
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                  disabled={userIdCheck === 'checking'}
                  onClick={() => verifyCreateUserId()}
                >
                  {userIdCheck === 'checking'
                    ? t('module.socialTrading.officialUserIdChecking')
                    : t('module.socialTrading.officialUserIdCheck')}
                </Button>
              </Stack>
              <TextField
                size="small"
                required
                label={t('module.socialTrading.nickname')}
                value={createNickname}
                onChange={(e) => setCreateNickname(e.target.value)}
                helperText={t('module.socialTrading.officialNicknameLength')}
              />
              <SocialTradingAvatarField
                value={createAvatar}
                onChange={setCreateAvatar}
                defaultUrls={userDefaultAvatars}
                uploadPath="/identity/upload"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/identity/users/official requires st-users:create */}
            <PermissionButton resource="st-users" action="create">
              <Button variant="contained" onClick={createOfficial}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <Dialog open={!!roleDialog} onClose={() => setRoleDialog(null)} fullWidth maxWidth="xs">
          <DialogTitle>{t('module.socialTrading.setRole')}</DialogTitle>
          <DialogContent>
            <TextField
              select
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              label={t('module.socialTrading.colRole')}
              value={nextRole}
              onChange={(e) => setNextRole(e.target.value)}
              SelectProps={{ native: true }}
            >
              <option value="USER">USER</option>
              <option value="OFFICIAL">OFFICIAL</option>
              <option value="KOL">KOL</option>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRoleDialog(null)}>{t('module.socialTrading.cancel')}</Button>
            {/* POST /social-trading/identity/users/role requires st-accounts:update */}
            <PermissionButton resource="st-accounts" action="update">
              <Button variant="contained" onClick={saveRole}>
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        {confirmDialog}
      </DashboardContent>
    </PermissionGuard>
  );
}
